/* eslint-disable max-lines */
import React, { useState, useEffect, useContext, useRef } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getUsersByID, getPlayerBalance } from "../../store/slices/user";
import useAuth from "../../hooks/useAuth";
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";
import { SocketContext } from "../../SocketContext";
import { requestWithdraw } from "../../store/services/withdrawService";

export default function Withdraw() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { singleUsers, playerBalance } = useSelector((s) => s.user);
  const socket = useContext(SocketContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const withdrawRequestInFlightRef = useRef(false);

  const account = Array.isArray(singleUsers) ? singleUsers[0] : null;

  // Determine user id (from current user slice, localStorage, or account)
  const storedId =
    user?.id || // prefer authenticated user id
    account?.id ||
    (typeof window !== "undefined" ? localStorage.getItem("userId") : null);

  React.useEffect(() => {
    if (storedId) {
      dispatch(getPlayerBalance(storedId));
    }
  }, [dispatch, storedId]);

  // Mock user data - in realแยท app this would come from auth context

  // Helper function to get bank logo based on bank code
  const getBankLogo = (bankCode) => {
    switch (bankCode) {
      case "true":
        return truemoneyIcon;
      case "014":
        return scbIcon;
      case "006":
        return ktbIcon;
      case "004":
        return kbankIcon;
      case "002":
        return bblIcon;
      case "011":
        return ttbIcon;
      case "030":
        return gsbIcon;
      case "025":
        return krungsriIcon;
      case "034":
        return baacIcon;
      case "024":
        return uobIcon;
      case "033":
        return ghbIcon;
      case "022":
        return cimbIcon;
      case "066":
        return islamIcon;
      case "promptpay":
        return promptpayIcon;
      default:
        return scbIcon; // fallback
    }
  };

  // Helper function to get bank name based on bank code
  const getBankName = (bankCode) => {
    switch (bankCode) {
      case "true":
        return "ทรูมันนี่วอลเล็ท";
      case "014":
        return "ธนาคารไทยพาณิชย์";
      case "006":
        return "ธนาคารกรุงไทย";
      case "004":
        return "ธนาคารกสิกรไทย";
      case "002":
        return "ธนาคารกรุงเทพ";
      case "011":
        return "ธนาคารทีทีบี";
      case "030":
        return "ธนาคารออมสิน";
      case "025":
        return "ธนาคารกรุงศรี";
      case "034":
        return "ธนาคารธ.ก.ส.";
      case "024":
        return "ธนาคารUOB";
      case "033":
        return "ธนาคารอาคารสงเคราะห์";
      case "022":
        return "ธนาคารCIMB";
      case "066":
        return "ธนาคารอิสลาม";
      case "promptpay":
        return "พร้อมเพย์";
      default:
        return "ธนาคาร";
    }
  };

  // Fetch user data from API
  useEffect(() => {
    if (storedId) dispatch(getUsersByID({ id: storedId }));
  }, [storedId, dispatch]);

  const handleWithdraw = async () => {
    if (isProcessing || withdrawRequestInFlightRef.current) return;

    withdrawRequestInFlightRef.current = true;
    setIsProcessing(true);
    setMessage("");
    setShowError(false);

    try {
      const result = await requestWithdraw({
        userId: user?.id,
        socket,
        userPayload: user,
      });

      if (result.success) {
        setMessage(result.message);
        setTimeout(() => {
          setMessage("");
        }, 5000);
      } else {
        setErrorMessage(result.message);
        setShowError(true);
        setMessage(result.message);
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      setErrorMessage("ไม่สามารถแจ้งถอนเงินได้ โปรดติดต่อเจ้าหน้าที่");
      setShowError(true);
      setMessage("เกิดข้อผิดพลาดในการเชื่อมต่อ");
    } finally {
      setTimeout(() => {
        dispatch(getPlayerBalance(user?.id));
        setIsProcessing(false);
        withdrawRequestInFlightRef.current = false;
      }, 2000);
    }
  };

  return (
    <div
      style={{
        padding: "20px 16px",
        background: "#ffffff",
        borderRadius: "12px",
        color: "#111827",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Header */}
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            marginBottom: "8px",
          }}
        >
          <Icon icon="solar:wallet-money-bold-duotone" width={32} height={32} />
          <div
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "var(--primaryColor)",
            }}
          >
            ถอนเงิน
          </div>
        </div>
        <div
          style={{
            fontSize: "12px",
            opacity: 0.9,
            color: "var(--primaryColor)",
          }}
        >
          ดำเนินการภายใน 5-10 นาที
        </div>
      </div>

      {/* Bank Information Card */}
      <div
        style={{
          background: "#f9fafb",
          borderRadius: "12px",
          padding: "16px",
          marginBottom: "20px",
          border: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "var(--primaryColor)",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: "20px", height: "20px" }}
            >
              <path
                d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ color: "var(--primaryColor)" }}>
              ข้อมูลธนาคาร โปรดใช้บัญชีของท่านในการถอนเงิน
            </span>
          </div>
        </div>

        {account?.book_number ? (
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  border: "1px solid #e5e7eb",
                }}
              >
                <img
                  src={getBankLogo(account?.bank_name)}
                  alt={getBankName(account?.bank_name)}
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ textAlign: "center", flex: 1 }}>
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    color: "#111827",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  {getBankName(account?.bank_name)}
                </h4>
                <p
                  style={{
                    margin: "0 0 6px 0",
                    color: "#374151",
                    fontSize: "16px",
                    fontFamily: "monospace",
                    letterSpacing: "1px",
                    fontWeight: "600",
                  }}
                >
                  {account?.book_number}
                </p>
                <p
                  style={{
                    margin: "0",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  ชื่อบัญชี: {account?.book_name}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: "#f9fafb",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
              border: "1px dashed #d1d5db",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "#e5e7eb",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px auto",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: "24px", height: "24px", color: "#6b7280" }}
              >
                <path
                  d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h4
              style={{
                margin: "0 0 8px 0",
                color: "#111827",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              ยังไม่มีบัญชีธนาคาร
            </h4>
            <p
              style={{
                margin: "0",
                color: "#6b7280",
                fontSize: "12px",
                opacity: 0.8,
              }}
            >
              กรุณาเพิ่มข้อมูลบัญชีธนาคารเพื่อทำธุรกรรม
            </p>
          </div>
        )}
      </div>

      {/* Balance Display */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          background: "#fef3c7",
          padding: "16px",
          borderRadius: "12px",
          border: "1px solid #fcd34d",
        }}
      >
        <div
          style={{ fontSize: "12px", color: "#92400e", marginBottom: "4px" }}
        >
          ยอดเงินคงเหลือ
        </div>
        <div style={{ fontSize: "28px", fontWeight: 700, color: "#b45309" }}>
          {parseInt(playerBalance)} THB
        </div>
      </div>

      {/* Status Messages */}
      {message && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            borderRadius: "8px",
            background: message.includes("เรียบร้อย") ? "#d1fae5" : "#fef3c7",
            fontSize: "14px",
            textAlign: "center",
            border: `1px solid ${
              message.includes("เรียบร้อย") ? "#10b981" : "#fcd34d"
            }`,
            color: message.includes("เรียบร้อย") ? "#065f46" : "#92400e",
          }}
        >
          {message}
        </div>
      )}

      {showError && errorMessage && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            borderRadius: "8px",
            background: "#fee2e2",
            fontSize: "14px",
            textAlign: "center",
            border: "1px solid #f87171",
            color: "#991b1b",
          }}
        >
          {errorMessage}
        </div>
      )}

      {/* Loading Indicator */}
      {isProcessing && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            borderRadius: "8px",
            background: "#f9fafb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "#1f2937",
            border: "1px solid #e5e7eb",
          }}
        >
          <Icon
            icon="solar:loading-bold-duotone"
            width={20}
            height={20}
            style={{ animation: "spin 1s linear infinite" }}
          />
          <span>กำลังดำเนินการ...</span>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleWithdraw}
        disabled={isProcessing}
        style={{
          width: "100%",
          padding: "16px",
          borderRadius: "12px",
          border: "none",
          background: isProcessing ? "#9ca3af" : "var(--primaryColor)",
          color: isProcessing ? "#000000" : "#ffffff",
          fontSize: "16px",
          fontWeight: 600,
          cursor: isProcessing ? "not-allowed" : "pointer",
          opacity: 1,
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Icon icon="solar:wallet-money-bold-duotone" width={20} height={20} />
        {isProcessing ? "กำลังดำเนินการ..." : "ยืนยันถอนเงิน"}
      </button>

      {/* Terms */}
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "#f9fafb",
          borderRadius: "12px",
          fontSize: "12px",
          lineHeight: "1.6",
          border: "1px solid #e5e7eb",
          color: "#374151",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
            color: "var(--primaryColor)",
          }}
        >
          <Icon icon="solar:info-circle-bold" width={16} height={16} />
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--primaryColor)",
            }}
          >
            เงื่อนไขการถอน
          </span>
        </div>
        <div style={{ color: "#6b7280" }}>
          • ท่านต้องทำตามเทิร์นที่กำหนดก่อนแจ้งถอนเงิน
          <br />
          • ระบบจะดำเนินการภายใน 5-10 นาที
          <br />
          • มั่นคงปลอดภัย 100%
          <br />• ระบบจะถอนไปยังบัญชีที่ท่านผูกไว้
          หากมีการเปลี่ยนแปลงบกรุณาแจ้งแอดมิน
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
